import { useState, useCallback, useRef, useEffect } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import { Descendant, Editor, Transforms, BaseRange } from 'slate';
import { Editable, ReactEditor, useFocused } from 'slate-react';
import isHotkey from 'is-hotkey';
import useModalState from '@sdk/components/ui/modal/useModalState';
import { UseAttachmentsType } from '@sdk/attachments/hooks/useAttachments';
import AttachmentsHorizontalPreview from '@sdk/attachments/components/AttachmentsHorizontalPreview';
import { Attachment } from '@sdk/attachments/types';
import onAttachmentsPaste from '@sdk/attachments/helpers/onAttachmentsPaste';
import { Persona } from '@sdk/personaCollection/types/Persona';
import Hideable from '@sdk/components/ui/Hideable';
import { GifData } from '@sdk/components/richText/types';

import { IS_SAFARI } from '@web/common/utils/currentBrowser';
import EditorToolbar from '@web/richText/components/EditorToolbar';
import Leaf from '@web/richText/components/Leaf';
import { toggleMark } from '@web/richText/helpers/utils';
import EditorElement from '@web/richText/components/EditorElement';
import serialize from '@web/richText/helpers/serialize';
import { EmojiPickerPosition } from '@web/richText/components/emojiPicker/EmojiPicker';
import GifPicker from '@web/richText/components/gifPicker/GifPicker';
import { Editor as SlingEditor } from '@web/richText/types';
import { onEnterKeyDown } from '@web/richText/helpers/keyboardHandlers';
import { EditorContainer } from '@web/richText/components/EditorContainer/EditorContainer';
import { MentionSuggestions } from '@web/richText/components/MentionSuggestions/MentionSuggestions';
import { MentionsState } from '@web/richText/TextEditorInner/hooks/useMentions';
import { getEmptyValue } from '@web/richText/textEditor.utils';

import { useLinkModalState } from './hooks/useLinkModalState';
import { useEditorEnterPress } from './hooks/useEditorEnterPress';

type EditableProps = {
  $resizable?: boolean;
};
const FixedHeightEditable = styled(Editable)<EditableProps>`
  padding: 10px;
  max-height: 220px;
  overflow-y: auto;
  resize: ${({ $resizable }): string => ($resizable ? 'vertical' : 'none')};
`;

const HOTKEYS = {
  'mod+b': 'bold',
  'mod+i': 'italic',
  'mod+u': 'underline',
  'mod+`': 'code',
  'mod+-': 'strikethrough',
};

const ARROW_UP_KEY_CODE = 38;

export type SubmitParams = {
  attachments: Attachment[];
  gif?: GifData;
  text: string;
};

export type OnContentChangeCallback = (content: {
  text: string;
  attachments: Attachment[];
}) => void;

const noopOnEvent = () => void 0;

export type Props = {
  attachmentsDisabled?: boolean;
  clearOnSubmit?: boolean;
  emojiPickerPosition?: EmojiPickerPosition;
  gifPickerEnabled?: boolean;
  hasAttachments?: boolean;
  hasError?: boolean;
  hiddenToolbarControls?: boolean;
  hideSubmit?: boolean;
  initialAttachments?: Attachment[];
  initialValue?: Descendant[] | string;
  onArrowUp?: () => void;
  onBlur?: (editor: SlingEditor) => void;
  onContentChange?: OnContentChangeCallback;
  onFocus?: (editor: SlingEditor) => void;
  onSubmit?: (textObj: SubmitParams) => void;
  personas?: Persona[];
  placeholder?: string;
  resizable?: boolean;
  submitLabel?: string;
  submitOnEnter?: boolean;
} & EditableProps;

type InnerProps = Props & {
  attachmentsState: UseAttachmentsType;
  editor: SlingEditor;
  mentionsState: MentionsState;
  value: Descendant[];
};

const TextEditorInner: React.FC<React.PropsWithChildren<InnerProps>> = ({
  attachmentsDisabled,
  attachmentsState,
  clearOnSubmit = true,
  editor,
  emojiPickerPosition,
  gifPickerEnabled = true,
  hasAttachments: hasAttachmentsProp,
  hiddenToolbarControls,
  hideSubmit,
  mentionsState,
  onArrowUp,
  onBlur = noopOnEvent,
  onContentChange,
  onFocus = noopOnEvent,
  onSubmit,
  placeholder,
  resizable,
  submitLabel,
  submitOnEnter = true,
  value,
}) => {
  const isFocused = useFocused();
  const linkModalState = useLinkModalState();
  const hasAttachments = !!attachmentsState.attachments.toSave.length || hasAttachmentsProp;

  const selectionRef = useRef<BaseRange>();
  const renderElement = useCallback(
    (props) => <EditorElement onLinkClick={linkModalState.onLinkClick} {...props} />,
    [linkModalState.onLinkClick]
  );
  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);
  const isEmpty = !serialize(value).trim().replace(/[#>]/g, '');
  const gifPickerState = useModalState();
  const emojiPickerState = useModalState();
  const [selectedGif, setSelectedGif] = useState<GifData>(null);

  useEffect(() => {
    Transforms.select(editor, Editor.end(editor, []));
    ReactEditor.focus(editor);
  }, [editor]);

  useEffect(() => {
    isFocused ? onFocus(editor) : onBlur(editor);
  }, [editor, isFocused, onBlur, onFocus]);

  useEffect(() => {
    if (onContentChange) {
      onContentChange({
        text: serialize(value),
        attachments: attachmentsState.attachments.toSave,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, attachmentsState.attachments.toSave]);

  const clear = useCallback(() => {
    editor.children = getEmptyValue();
    editor.history = {
      redos: [],
      undos: [],
    };
  }, [editor]);

  const submitGif = (gif?: GifData): void => {
    onSubmit?.({ text: '', gif: gif || selectedGif, attachments: [] });
    gifPickerState.close();
  };

  const submit = (): void => {
    if (gifPickerState.isVisible) {
      submitGif();
      return;
    }

    onSubmit?.({ text: serialize(value), attachments: attachmentsState.attachments.toSave });
    if (clearOnSubmit) {
      Transforms.select(editor, Editor.start(editor, []));
      clear();
      attachmentsState.clearAttachments(true);
    }
    ReactEditor.focus(editor);
  };

  const focusWithSelection = (): void => {
    if (isFocused) {
      return;
    }

    if (selectionRef.current) {
      Transforms.select(editor, selectionRef.current);
    } else {
      Transforms.select(editor, Editor.end(editor, []));
    }
    ReactEditor.focus(editor);
  };

  const blurEditor = (): void => {
    isFocused && ReactEditor.blur(editor);
  };

  const insertText = (text: string): void => {
    focusWithSelection();
    editor.insertText(text);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    for (const hotkey in HOTKEYS) {
      if (isHotkey(hotkey, event as any)) {
        event.preventDefault();
        const mark = HOTKEYS[hotkey];
        toggleMark(editor, mark);
        return;
      }
    }

    if (event.keyCode === ARROW_UP_KEY_CODE) {
      if (isEmpty) {
        onArrowUp?.();
      } else {
        event.stopPropagation();
      }
    }
    onEnterKeyDown(event, editor, submitOnEnter);
    mentionsState.onKeyDown(event);
  };

  const mentionSuggestionsVisible =
    mentionsState.target && mentionsState.filteredPersonas.length > 0;

  const submitActive = (): boolean => {
    if (mentionSuggestionsVisible) return false;

    if (gifPickerState.isVisible) {
      return !!selectedGif;
    }

    return !isEmpty || hasAttachments;
  };
  const isSubmitActive = submitActive();

  useEditorEnterPress({
    isSubmitActive,
    submit,
    submitOnEnter,
  });

  return (
    <>
      {!gifPickerState.isVisible && (
        <EditorContainer>
          <FixedHeightEditable
            $resizable={resizable}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            placeholder={placeholder}
            spellCheck
            onBlur={(): void => {
              if (editor.selection) {
                selectionRef.current = editor.selection;
              }
            }}
            onKeyDown={onKeyDown}
            onPaste={(event): void => {
              onAttachmentsPaste(event, {
                onPaste: attachmentsState.addAttachmentsFromFilelist,
                isActive: isFocused && !attachmentsDisabled,
              });

              if (IS_SAFARI) {
                if (event.clipboardData?.files?.length) {
                  return;
                }

                event.preventDefault();
                ReactEditor.insertData(editor, event.clipboardData);
              }
            }}
          />
        </EditorContainer>
      )}

      {mentionSuggestionsVisible && <MentionSuggestions mentionsState={mentionsState} />}

      {!attachmentsDisabled && (
        <Hideable isHidden={gifPickerState.isVisible}>
          <AttachmentsHorizontalPreview attachmentsState={attachmentsState} />
        </Hideable>
      )}

      {gifPickerEnabled && (
        <GifPicker
          pickerState={gifPickerState}
          selectGif={setSelectedGif}
          sendGif={submitGif}
          onClose={focusWithSelection}
        />
      )}
      <EditorToolbar
        hideSubmit={!onSubmit || hideSubmit}
        submit={submit}
        submitActive={isSubmitActive}
        submitLabel={submitLabel}
        insertText={insertText}
        focusWithSelection={focusWithSelection}
        blurEditor={blurEditor}
        emojiPickerPosition={emojiPickerPosition}
        gifPickerEnabled={gifPickerEnabled}
        gifPickerState={gifPickerState}
        emojiPickerState={emojiPickerState}
        linkModalState={linkModalState}
        attachmentsState={attachmentsState}
        attachmentsDisabled={attachmentsDisabled}
        hiddenToolbarControls={hiddenToolbarControls}
      />
    </>
  );
};

export default TextEditorInner;
